/**
 * Composable for easy Sentry integration throughout the app
 */
export const useSentry = () => {
  // Get Sentry instance from the plugin
  const { $sentry } = useNuxtApp()

  if (!$sentry) {
    console.warn('Sentry not initialized')
    return {
      captureException: () => {},
      captureMessage: () => {},
      captureApiError: () => {},
      addBreadcrumb: () => {},
      setUser: () => {},
      setTag: () => {},
      setContext: () => {},
    }
  }

  /**
   * Capture an exception with Sentry
   * @param {Error} error - The error to capture
   * @param {Object} options - Additional options for Sentry
   */
  const captureException = (error, options = {}) => {
    try {
      return $sentry.captureException(error, options)
    } catch (e) {
      console.error('Failed to capture exception with Sentry:', e)
    }
  }

  /**
   * Capture a message with Sentry
   * @param {string} message - The message to capture
   * @param {string} level - The severity level (debug, info, warning, error, fatal)
   * @param {Object} options - Additional options for Sentry
   */
  const captureMessage = (message, level = 'info', options = {}) => {
    try {
      return $sentry.captureMessage(message, level, options)
    } catch (e) {
      console.error('Failed to capture message with Sentry:', e)
    }
  }

  /**
   * Capture API errors with additional context
   * @param {Error} error - The API error
   * @param {Object} requestConfig - The request configuration
   * @param {Object} response - The response object
   */
  const captureApiError = (error, requestConfig = {}, response = null) => {
    try {
      const errorContext = {
        tags: {
          errorType: 'api',
          method: requestConfig.method?.toUpperCase() || 'UNKNOWN',
          url: requestConfig.url || 'unknown',
          statusCode: response?.status || 'unknown',
        },
        extra: {
          requestConfig: {
            method: requestConfig.method,
            url: requestConfig.url,
            headers: requestConfig.headers,
            data: requestConfig.data,
          },
          response: response ? {
            status: response.status,
            statusText: response.statusText,
            data: response.data,
          } : null,
        },
      }

      return $sentry.captureException(error, errorContext)
    } catch (e) {
      console.error('Failed to capture API error with Sentry:', e)
    }
  }

  /**
   * Add a breadcrumb to Sentry
   * @param {Object} breadcrumb - The breadcrumb object
   */
  const addBreadcrumb = (breadcrumb) => {
    try {
      return $sentry.addBreadcrumb(breadcrumb)
    } catch (e) {
      console.error('Failed to add breadcrumb to Sentry:', e)
    }
  }

  /**
   * Set user context in Sentry
   * @param {Object} user - The user object
   */
  const setUser = (user) => {
    try {
      return $sentry.setUser(user)
    } catch (e) {
      console.error('Failed to set user in Sentry:', e)
    }
  }

  /**
   * Set a tag in Sentry
   * @param {string} key - The tag key
   * @param {string} value - The tag value
   */
  const setTag = (key, value) => {
    try {
      return $sentry.setTag(key, value)
    } catch (e) {
      console.error('Failed to set tag in Sentry:', e)
    }
  }

  /**
   * Set context in Sentry
   * @param {string} key - The context key
   * @param {Object} context - The context object
   */
  const setContext = (key, context) => {
    try {
      return $sentry.setContext(key, context)
    } catch (e) {
      console.error('Failed to set context in Sentry:', e)
    }
  }

  return {
    captureException,
    captureMessage,
    captureApiError,
    addBreadcrumb,
    setUser,
    setTag,
    setContext,
  }
}
